import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { ServiceNoticeBanner } from '@/features/auth/ServiceNoticeBanner';
import { useUndrawnTicketDrawer } from '@/features/tabs/drawUndrawnJobs';

import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { isCompactViewport, subscribeCompactViewport } from './viewport';

/**
 * The desktop sidebar's remembered shape: the labeled sectioned menu (wide) or
 * the slim icon rail. Wide by default — the sections the owner asked for
 * (2026-08-14) must be findable without knowing a control exists; the rail is
 * the density optimisation someone chooses once, and the choice sticks per
 * browser.
 */
const WIDE_KEY = 'gc.staff.nav.wide';

function readWide(): boolean {
  try {
    return localStorage.getItem(WIDE_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeWide(wide: boolean): void {
  try {
    localStorage.setItem(WIDE_KEY, wide ? '1' : '0');
  } catch {
    // Storage refused; the shape simply resets next reload.
  }
}

/**
 * App frame: top bar over the sidebar and the routed page.
 *
 * On a phone the sidebar is an OVERLAY, closed by default. Inline it ate 64
 * of 375 pixels on every screen — a sixth of the page gone before any content
 * — which is most of why the app "looked buggy" on phones. The hamburger
 * opens it over the content; tapping the backdrop or navigating closes it.
 *
 * "A phone" is `compact` (viewport.ts): upright OR on its side. Turned
 * sideways a phone is 780–932px wide, which used to count as a desktop — the
 * 256px menu came back inline and left the page a strip beside it, in about
 * 330px of height (owner, 2026-09-23: "unusable when we change from portrait
 * to landscape"). Now a sideways phone keeps the overlay too.
 *
 * Everywhere else the sidebar is inline and visible by default, in whichever of
 * its two shapes this browser last chose (labeled drawer or icon rail — the
 * pinch control inside the sidebar switches them). The hamburger keeps its
 * one job either way: show or hide the whole column, e.g. for a wide table.
 */
export function AppShell() {
  // Whichever staff tab is open draws the tickets nobody else can (portal
  // orders) — see drawUndrawnJobs.ts.
  useUndrawnTicketDrawer();

  const [isMobile, setIsMobile] = useState(isCompactViewport);
  const [sidebarVisible, setSidebarVisible] = useState(() => !isCompactViewport());
  const [wide, setWide] = useState(readWide);
  const location = useLocation();

  function toggleWide(): void {
    setWide((current) => {
      const next = !current;
      writeWide(next);
      return next;
    });
  }

  useEffect(
    () =>
      // Listens to `resize` and `orientationchange` as well as the query's own
      // `change`: emulated viewports update `matches` without firing `change`,
      // which left the rail stranded on the wrong side of the boundary —
      // observed, not hypothetical. The guard makes the event spam free.
      subscribeCompactViewport((compact) => {
        setIsMobile((was) => {
          if (was === compact) return was;
          // Crossing the boundary resets to that side's default: overlay
          // closed, inline open. A rotation should never strand a phone with
          // no content.
          setSidebarVisible(!compact);
          return compact;
        });
      }),
    [],
  );

  // Picking a destination is the end of the overlay's job.
  useEffect(() => {
    if (isMobile) setSidebarVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close on route change only
  }, [location.pathname]);

  return (
    // TRANSPARENT on purpose: the blueprint-grid backdrop lives on <body>
    // (theme.css), and an opaque fill here was painting over the design's
    // signature element on every signed-in screen — the app read as "old
    // layout, new colours" instead of the schematic the owner approved.
    <div className="flex h-full flex-col">
      {/* The subscription warning (EclipseGG platform, 2026-10-19), above
          everything on every page; nothing at all when there is none. */}
      <ServiceNoticeBanner />
      <TopBar onToggleSidebar={() => setSidebarVisible((visible) => !visible)} />

      <div className="relative flex min-h-0 flex-1">
        {isMobile && sidebarVisible ? (
          <div
            className="absolute inset-0 z-30 bg-black/50"
            onClick={() => setSidebarVisible(false)}
            role="presentation"
          />
        ) : null}

        {sidebarVisible ? (
          // `contents` on desktop keeps the rail a real flex child, exactly as
          // it always was; the wrapper only exists to float it on phones —
          // where the nav is a labeled drawer, not the 64px icon sliver.
          <div
            className={isMobile ? 'absolute inset-y-0 z-40' : 'contents'}
            style={isMobile ? { insetInlineStart: 0 } : undefined}
          >
            <Sidebar
              variant={isMobile || wide ? 'drawer' : 'rail'}
              onToggleWide={isMobile ? undefined : toggleWide}
            />
          </div>
        ) : null}

        {/* min-w-0 stops wide tables (Bills has 18 columns) from forcing the
            whole page to scroll horizontally instead of scrolling themselves. */}
        <main className="min-w-0 flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
